/**
 * Documentos do modulo de Chamados (service desk).
 *
 * Espelham as colecoes criadas por
 * `database-scripts/chamados/01_create_collections.js`. Qualquer campo novo
 * entra NOS DOIS lugares — o script e a fonte de verdade dos indices, e este
 * arquivo e a fonte de verdade do formato para o front.
 *
 * Convencao do backend: o `entityName` enviado ao `findnative` e o nome da
 * colecao, e o BrokerHelper o converte para MINUSCULAS. Por isso as constantes
 * abaixo ja estao em minusculo — para o que se le no codigo ser exatamente o
 * que existe no banco.
 *
 * Ver `docs/chamados/plano.md` para o desenho completo.
 */

/**
 * Banco Mongo dedicado ao modulo.
 *
 * Um banco unico, e nao um por contrato como no agent-builder: quem atende e a
 * TemAPI e a mesa de atendimento e cross-cliente. O isolamento e por campo
 * (`codecompany`/`codecontract`) em cada documento.
 */
export const CHAMADOS_DB_NAME = 'temapi_chamados';

/*
 * MINUSCULAS, sempre — e o mesmo nome nos tres caminhos:
 *
 *   - `findnative` recebe a colecao e o BrokerHelper ainda faz toLowerCase();
 *   - `save`/`remove` resolvem a ENTIDADE pelo nome registrado no Java, e a
 *     convencao do projeto e registrar entidade Mongo em minusculas.
 *
 * Ja houve uma constante ENTITY_* separada, capitalizada no padrao do SQL
 * (`Mktpagents`). Estava errada e o sintoma foi o remove falhar no Java.
 */
export const COLLECTION_CONFIG = 'mktpticketconfig';
export const COLLECTION_QUEUES = 'mktpticketqueues';
export const COLLECTION_AGENTS = 'mktpticketagents';
export const COLLECTION_CATEGORIES = 'mktpticketcategories';
export const COLLECTION_CHANNELS = 'mktpticketchannels';
export const COLLECTION_CALENDARS = 'mktpticketcalendars';
export const COLLECTION_MACROS = 'mktpticketmacros';
export const COLLECTION_CONTACTS = 'mktpticketcontacts';
export const COLLECTION_TICKETS = 'mktptickets';
export const COLLECTION_MESSAGES = 'mktpticketmessages';
export const COLLECTION_SESSIONS = 'mktpticketsessions';
export const COLLECTION_INBOX = 'mktpticketinbox';
export const COLLECTION_COUNTERS = 'mktpticketcounters';



// ───────────────────────────── enums ─────────────────────────────

/**
 * Estados do chamado. A transicao entre eles pertence ao `TicketServices`
 * (Java) — o front apenas exibe e pede a mudanca.
 */
export type TicketStatus =
    | 'BOT'
    | 'AGUARDANDO_ATENDIMENTO'
    | 'EM_ATENDIMENTO'
    | 'AGUARDANDO_CLIENTE'
    | 'PAUSADO'
    | 'RESOLVIDO'
    | 'ENCERRADO'
    | 'CANCELADO';

export type TicketPriority = 'BAIXA' | 'NORMAL' | 'ALTA' | 'CRITICA';

export type TicketChannel = 'APP' | 'WHATSAPP' | 'TELEGRAM';

export type MessageAuthorType = 'CLIENTE' | 'ATENDENTE' | 'BOT' | 'SISTEMA';

export type MessageDeliveryStatus = 'PENDENTE' | 'ENVIADO' | 'ENTREGUE' | 'LIDO' | 'ERRO';

export type TicketEventType =
    | 'ABERTURA'
    | 'STATUS'
    | 'ATRIBUICAO'
    | 'TRANSFERENCIA'
    | 'ESCALONAMENTO'
    | 'SLA'
    | 'HANDOFF'
    | 'REABERTURA'
    | 'ENCERRAMENTO'
    | 'CSAT';

export type QueueDistribution = 'MANUAL' | 'ROUNDROBIN' | 'MENOR_CARGA';

/** Como a fila trata a empresa no momento da abertura. */
export type EmpresaNaAbertura = 'NAO' | 'OPCIONAL' | 'OBRIGATORIA';

/**
 * Papel do atendente.
 *
 * GERENTE nao e "supervisor de tudo" por acaso: e um papel proprio porque a
 * visibilidade dele nao depende de fila nenhuma — ele enxerga o atendimento
 * inteiro, inclusive fila que ninguem lembrou de vincular a ele.
 */
export type QueueMemberRole = 'ATENDENTE' | 'SUPERVISOR' | 'GERENTE';

export type CategoryAccessType = 'PERFIL' | 'USUARIO' | 'CONTRATO';

export type InboxStatus = 'PENDENTE' | 'PROCESSADO' | 'ERRO' | 'IGNORADO';

export type SessionState = 'IDENTIFICANDO' | 'MENU' | 'BOT' | 'CHAMADO' | 'ENCERRADA';

/** Campos de auditoria repetidos em toda colecao de configuracao. */
export interface AuditFields {
    createdBy?: string;
    createdOn?: string;
    modifiedBy?: string;
    modifiedOn?: string;
}

// ─────────────────────── configuracao (o front grava) ───────────────────────

/**
 * Configuracao do CONTRATO.
 *
 * Uma por cliente. A empresa saiu da chave: cliente com duas unidades tinha
 * duas configuracoes que precisavam ser mantidas iguais na mao, e bastava
 * esquecer uma para o mesmo cliente atender de dois jeitos.
 */
export interface TicketConfig extends AuditFields {
    _id?: string;
    /** O contrato e a chave. Uma configuracao por cliente. */
    codecontract: number;
    /**
     * Esta central atende QUALQUER contrato.
     *
     * O service desk e cross-contrato: quem abre pertence ao contrato dele, a
     * central e de outro. Sem dizer quem ela atende, ninguem de fora consegue
     * abrir chamado aqui — e a tela de abertura fica sem opcao nenhuma.
     *
     * O padrao e `false` de proposito. Central que atende o mundo inteiro por
     * omissao e o tipo de padrao que vira vazamento no dia em que a segunda
     * central existir, e ninguem revisa o que ja funciona.
     */
    atendeTodosContratos?: boolean;
    /** Contratos atendidos, quando nao e "todos". O proprio sempre entra. */
    contratosAtendidos?: number[];
    prefixoChamado: string;
    filaPadraoId?: string;
    categoriaPadraoId?: string;
    prioridadePadrao: TicketPriority;
    calendarioId?: string;
    bot: TicketBotConfig;
    /** Chamado encerrado que recebe mensagem dentro do prazo REABRE. */
    reabrePrazoHoras: number;
    /**
     * Segregacao de abertura nos canais externos.
     *
     * `true` = numero desconhecido NAO abre chamado. O que define "conhecido"
     * e existir em `mktpticketcontacts` com `autorizado: true`; o vinculo com
     * o MKTPUSER (`iduser`) e o que amarra o telefone a uma pessoa de verdade.
     */
    exigeContatoCadastrado: boolean;
    /**
     * Resposta automatica para quem escreve sem cadastro.
     *
     * Sem ela o desconhecido fica no vacuo: a mensagem dele nao vira chamado e
     * ele nao recebe nada de volta — o pior dos dois mundos. Quem envia e o
     * fluxo do canal; a tela so guarda o texto.
     */
    mensagemSemCadastro?: string;
    /**
     * Estouro do prazo de SOLUCAO sobe o chamado para a fila de escalonamento.
     *
     * Vale so para o prazo de solucao: escalar no estouro da primeira resposta
     * subiria chamado que ninguem sequer olhou, tirando-o justamente da fila
     * de quem deveria olhar.
     */
    slaEscalonaAuto?: boolean;
    /**
     * De quantos em quantos segundos as telas se atualizam sozinhas.
     *
     * Vale para o balao flutuante e e o PADRAO da mesa (onde cada atendente
     * pode escolher o proprio, e o dele manda). Zero desliga.
     *
     * Era 60 segundos fixo no codigo, e 60 e muito para quem esta conversando:
     * a resposta do cliente levava um minuto para aparecer. Quem opera sabe
     * qual o intervalo certo para o volume da casa — 15 segundos numa central
     * pequena e barato, e numa grande e o que derruba o banco.
     */
    intervaloAtualizacaoSegundos?: number;
    csatAtivo: boolean;
    active: boolean;
}

export interface TicketBotConfig {
    ativo: boolean;
    /** `_id` do agente no agent-builder — o mesmo que vai no AiServices/converse. */
    agente?: string;
    /** Turnos sem resolver antes de passar para humano. */
    maxTurnos: number;
    /** Primeira mensagem do bot na conversa. Vazio = o agente comeca sozinho. */
    saudacao?: string;
    /**
     * Frases que passam para humano na hora.
     *
     * Array de OBJETOS, e nao de strings: e configuracao gravada PELO FRONT, e
     * o conversor JSON->Document do `save` recusa array de escalares.
     */
    gatilhos?: TicketBotTrigger[];
    /** Fora do horario do calendario, o bot nem tenta. Depende da F7. */
    handoffForaDoHorario?: boolean;
}

export interface TicketBotTrigger {
    frase: string;
}

/** Fila/equipe. Os membros sao embutidos: lista pequena, sempre lida junto. */
export interface TicketQueue extends AuditFields {
    _id?: string;
    /**
     * O DONO da configuracao: o contrato (o cliente).
     *
     * NAO ha `codecompany` aqui. A configuracao e do cliente, nao da unidade:
     * fila, categoria, calendario e resposta pronta valem para o contrato
     * inteiro. Guardar a empresa junto criaria uma copia por unidade que
     * alguem teria que manter igual na mao.
     */
    codecontract: number;
    nome: string;
    descricao?: string;
    tipoDistribuicao: QueueDistribution;
    /** Para onde o chamado sobe no escalonamento. */
    filaEscalacaoId?: string;
    /**
     * A abertura pergunta de qual EMPRESA e o chamado?
     *
     * Faz diferenca quando o cliente tem varias unidades e o atendimento
     * precisa saber de qual delas veio o problema — nota fiscal de uma filial,
     * acesso de outra. Fila que atende o cliente inteiro nao pergunta nada.
     */
    empresaNaAbertura?: EmpresaNaAbertura;
    membros: TicketQueueMember[];
    active: boolean;
}

export interface TicketQueueMember {
    iduser: number;
    nome: string;
    papel: QueueMemberRole;
    recebeAuto: boolean;
}

/**
 * Atendente: quem atende, em quais filas e com qual papel.
 *
 * Cadastro PROPRIO, e nao a lista de membros dentro da fila, por dois motivos
 * que a fila nao resolvia:
 *
 *  - **hierarquia.** `supervisorIduser` diz de quem a pessoa e subordinada.
 *    Dentro da fila isso teria que ser repetido em cada fila;
 *  - **gerente.** Papel que nao pertence a fila nenhuma — ele ve todas.
 *
 * `TicketQueue.membros` continua existindo como PROJECAO desta lista: o Java
 * le os supervisores por ali para notificar. Quem grava a projecao e a tela de
 * atendentes, ao salvar. A verdade e este documento.
 */
export interface TicketAgent extends AuditFields {
    _id?: string;
    /**
     * O DONO da configuracao: o contrato (o cliente).
     *
     * NAO ha `codecompany` aqui. A configuracao e do cliente, nao da unidade:
     * fila, categoria, calendario e resposta pronta valem para o contrato
     * inteiro. Guardar a empresa junto criaria uma copia por unidade que
     * alguem teria que manter igual na mao.
     */
    codecontract: number;
    iduser: number;
    /** Nome denormalizado do MKTPUSER, para a lista nao consultar o SQL por linha. */
    nome: string;
    papel: QueueMemberRole;
    /** A quem esta pessoa se reporta. Vazio para gerente. */
    supervisorIduser?: number;
    /** Filas em que atua. Array de OBJETOS — a regra do modulo. */
    filas: TicketLabelRef[];
    /** Entra no rodizio de distribuicao automatica da fila. */
    recebeAuto?: boolean;
    active: boolean;
}

/** Assunto do chamado: roteamento, SLA, visibilidade por canal e segregacao. */
export interface TicketCategory extends AuditFields {
    _id?: string;
    /**
     * O DONO da configuracao: o contrato (o cliente).
     *
     * NAO ha `codecompany` aqui. A configuracao e do cliente, nao da unidade:
     * fila, categoria, calendario e resposta pronta valem para o contrato
     * inteiro. Guardar a empresa junto criaria uma copia por unidade que
     * alguem teria que manter igual na mao.
     */
    codecontract: number;
    parentId?: string;
    nome: string;
    filaId?: string;
    prioridade: TicketPriority;
    sla: TicketCategorySla;
    /**
     * Canais em que a categoria aparece para quem vai abrir.
     *
     * MAPA, e nao array de strings, por limitacao do backend: o conversor
     * JSON->Document do `save` assume que todo JSONArray contem objetos, e
     * recusa `["APP","WHATSAPP"]` com "JSONArray must contain only
     * JSONObjects to convert to Document array".
     *
     * O mapa tambem consulta bem: `{"visivelEm.WHATSAPP": true}`.
     *
     * REGRA GERAL do modulo: nada gravado PELO FRONT pode ter array de
     * escalares. Array de OBJETOS (membros, acesso, anexos) passa normal.
     */
    visivelEm: TicketChannelVisibility;
    /** Campos extras no formato do dynamic-forms. */
    camposExtra?: unknown[];
    /** VAZIO = liberada para a empresa; preenchida = so quem casa. */
    acesso: TicketCategoryAccess[];
    active: boolean;
}

/** Um booleano por canal. Ausente = nao aparece naquele canal. */
export interface TicketChannelVisibility {
    APP?: boolean;
    WHATSAPP?: boolean;
    TELEGRAM?: boolean;
}

export interface TicketCategorySla {
    primeiraRespostaMin: number;
    solucaoMin: number;
}

export interface TicketCategoryAccess {
    tipo: CategoryAccessType;
    valor: string;
    podeAbrir: boolean;
}

/** Instancia de canal (numero de WhatsApp, bot de Telegram). */
export interface TicketChannelConfig extends AuditFields {
    _id?: string;
    /**
     * O DONO da configuracao: o contrato (o cliente).
     *
     * NAO ha `codecompany` aqui. A configuracao e do cliente, nao da unidade:
     * fila, categoria, calendario e resposta pronta valem para o contrato
     * inteiro. Guardar a empresa junto criaria uma copia por unidade que
     * alguem teria que manter igual na mao.
     */
    codecontract: number;
    tipo: TicketChannel;
    /** Numero E.164 ou @bot. */
    identificador: string;
    nome: string;
    /** IDCONF do fluxo TemAPI que envia a mensagem. */
    envio?: { idconf: number };
    /**
     * Como a mensagem que CHEGA encontra este canal.
     *
     * O `identificador` acima e o numero como as pessoas o escrevem
     * (`+5511999999999`). O provedor nao manda assim: a Cloud API manda
     * `phone_number_id` e um `display_phone_number` sem o `+`. Casar por
     * texto formatado e o erro classico — a mensagem chega, nao acha canal
     * nenhum e some sem erro em lugar algum. Por isso o vinculo de entrada e
     * por identificador ESTAVEL:
     *
     *   - `idhook`: o webhook TemAPI que recebeu (`Mktpwebhooklog.IDHOOK`);
     *   - `phoneNumberId`: o id do numero na Meta, que nunca muda de formato.
     *
     * Ver `docs/chamados/fluxo-whatsapp-entrada.md`.
     */
    entrada?: TicketChannelInbound;
    /**
     * Referencia ao segredo — NUNCA o token em claro.
     * Esta colecao e lida pelo front; o token pertence ao fluxo de saida.
     */
    tokenRef?: string;
    filaPadraoId?: string;
    categoriaPadraoId?: string;
    active: boolean;
}

/** Vinculo de ENTRADA do canal: como o inbound acha este documento. */
export interface TicketChannelInbound {
    /** `Mktpwebhooklog.IDHOOK` do webhook que recebe as mensagens. */
    idhook?: number;
    /** WhatsApp Cloud API: `metadata.phone_number_id` do payload da Meta. */
    phoneNumberId?: string;
}

/**
 * Calendario de atendimento. Sem ele o SLA conta madrugada e feriado como
 * tempo util.
 */
export interface TicketCalendar extends AuditFields {
    _id?: string;
    /**
     * O DONO da configuracao: o contrato (o cliente).
     *
     * NAO ha `codecompany` aqui. A configuracao e do cliente, nao da unidade:
     * fila, categoria, calendario e resposta pronta valem para o contrato
     * inteiro. Guardar a empresa junto criaria uma copia por unidade que
     * alguem teria que manter igual na mao.
     */
    codecontract: number;
    nome: string;
    timezone: string;
    semana: TicketCalendarDay[];
    excecoes: TicketCalendarException[];
    active: boolean;
}

export interface TicketCalendarDay {
    /** 1 = segunda ... 7 = domingo */
    dia: number;
    horaIni: string;
    horaFim: string;
}

export interface TicketCalendarException {
    data: string;
    atende: boolean;
}

/** Resposta pronta. Template HSM do WhatsApp entra aqui, com tipo proprio. */
export interface TicketMacro extends AuditFields {
    _id?: string;
    /**
     * O DONO da configuracao: o contrato (o cliente).
     *
     * NAO ha `codecompany` aqui. A configuracao e do cliente, nao da unidade:
     * fila, categoria, calendario e resposta pronta valem para o contrato
     * inteiro. Guardar a empresa junto criaria uma copia por unidade que
     * alguem teria que manter igual na mao.
     */
    codecontract: number;
    filaId?: string;
    titulo: string;
    texto: string;
    /** 'TEXTO' = resposta livre; 'TEMPLATE_HSM' = template aprovado na Meta. */
    tipo: 'TEXTO' | 'TEMPLATE_HSM';
    templateName?: string;
    /**
     * Parametros usados no texto, como `{{numero}}`.
     *
     * Sem eles o marcador nao diz o que entra no lugar — e o atendente ou
     * manda o texto cru, ou adivinha. Cada parametro declara de ONDE sai o
     * valor; o que nao tem origem automatica fica visivel para ele completar.
     */
    parametros?: TicketMacroParam[];
    /** Aplicada junto com o envio. */
    acao?: TicketMacroAction;
    active: boolean;
}

export interface TicketMacroParam {
    /** O que vai entre as chaves: `{{chave}}`. */
    chave: string;
    /** Id do catalogo em `config/macros/parametros.ts` — `MANUAL` inclusive. */
    origem: string;
    /** Explicacao livre, para quem for usar a resposta pronta. */
    descricao?: string;
}

export interface TicketMacroAction {
    status?: TicketStatus;
    prioridade?: TicketPriority;
    filaId?: string;
}

// ───────────────── operacao (so o TicketServices grava) ─────────────────

/** Contato externo: a ponte entre um telefone e um cliente. */
export interface TicketContact extends AuditFields {
    _id?: string;
    /**
     * Por onde esta pessoa fala com o atendimento.
     *
     * VARIOS: a mesma pessoa manda mensagem do WhatsApp hoje e do Telegram
     * amanha, e tem que cair no MESMO contato — senao vira dois clientes, com
     * dois historicos, e a autorizacao precisa ser dada duas vezes.
     *
     * Array de OBJETOS, como manda a regra do modulo.
     */
    canais: TicketContactChannel[];
    /**
     * Canal e identificador PRINCIPAIS.
     *
     * Copia do primeiro item de `canais`, mantida enquanto o Java procurar o
     * contato por estes campos (`resolverContato`). Ver
     * `docs/chamados/java-proposto/README.md`.
     *
     * @deprecated use `canais`.
     */
    canal?: TicketChannel;
    /** @deprecated use `canais`. */
    identificador?: string;
    nome?: string;
    /**
     * O DONO do cadastro: a central de atendimento.
     *
     * Mesma regra das outras filhas da configuracao (filas, categorias,
     * macros): `codecontract` e quem responde pelo documento, e e por ele que
     * o filtro nativo de tenant recorta — o `EventDataBusiness` so conhece os
     * nomes `codecompany` e `codecontract`. Renomear aqui deixaria a colecao
     * SEM filtro nenhum.
     */
    codecontract?: number;
    /**
     * De qual CLIENTE e a pessoa.
     *
     * O contato e de um cliente, mas o cadastro e da central que o atende —
     * por isso os dois campos. E daqui que sai o cliente do chamado quando a
     * mensagem chega pelo WhatsApp, onde nao ha tela para perguntar.
     */
    codecontractCliente?: number;
    /** Preenchido quando o contato tambem e usuario TemAPI. */
    iduser?: number;
    email?: string;
    autorizado: boolean;
}

/** Um canal de entrada do contato. */
export interface TicketContactChannel {
    canal: TicketChannel;
    /** Telefone em E.164 (`+5511...`) ou `@usuario` do Telegram. */
    identificador: string;
}

/** Rotulo denormalizado: a verdade e o id; o nome e so exibicao. */
export interface TicketLabelRef {
    id?: string;
    nome?: string;
}

export interface TicketOwnerRef {
    iduser?: number;
    nome?: string;
}

export interface Ticket {
    _id?: string;
    /** Numero visivel, prefixo + sequencia por empresa. */
    numero: string;
    codecompany: number;
    /** De qual cliente e o chamado. */
    codecontract?: number;
    /**
     * Contrato do Cliente que originou o chamado.
     *
     */
    codecontractConfiguracao?: number;
    /**
     * Contrato da configuracao do atendimento.
     */
    categoria?: TicketLabelRef;
    fila?: TicketLabelRef;
    responsavel?: TicketOwnerRef;
    origem: TicketOrigin;
    assunto: string;
    /** O PROBLEMA, escrito por quem abriu. */
    descricao?: string;
    /**
     * O que foi FEITO para resolver — a contrapartida da `descricao`.
     *
     * Sem campo proprio, a solucao ficava perdida no meio da conversa: quem
     * abre o mesmo defeito seis meses depois tem que reler tudo para descobrir
     * o que resolveu — se e que resolveu. E o atendente que assume um chamado
     * ja resolvido nao tem como saber o que o anterior fez.
     *
     * Escrita ao RESOLVER, e gravada pelo Java junto com a mudanca de status.
     */
    solucao?: string;
    /**
     * O COMANDO executado para resolver — script, SQL, rotina, chamada.
     *
     * Separado da `solucao` de propósito: a solução é o que se conta para o
     * cliente ("segunda via reenviada"); isto é o que se REPETE quando o mesmo
     * defeito volta. Misturar os dois faz o texto técnico chegar em quem não
     * o entende, e some justamente para quem precisa dele.
     */
    comandoExecutado?: string;
    /**
     * Array de escalares — so pode ser gravado PELO JAVA, que escreve com o
     * driver do Mongo direto. Se algum dia uma tela precisar editar tags, ela
     * esbarra na mesma limitacao que trocou `visivelEm` por mapa.
     */
    tags?: string[];
    camposExtra?: Record<string, unknown>;
    status: TicketStatus;
    prioridade: TicketPriority;
    /**
     * A prioridade como NUMERO, so para ordenar: 4 critica ... 1 baixa.
     *
     * A prioridade e gravada como texto, e ordenar texto sai em ordem
     * alfabetica — ALTA, BAIXA, CRITICA, NORMAL. A lista PARECERIA ordenada e
     * estaria errada, que e o pior resultado possivel.
     *
     * Denormalizacao deliberada: e o Java quem mantem os dois em sincronia, no
     * mesmo ponto em que grava a prioridade. Antes disto a tela resolvia com
     * um `aggregate` que calculava o numero a cada consulta — caro, e fora do
     * alcance do indice.
     */
    prioridadeOrdem?: number;
    sla?: TicketSla;
    datas?: TicketDates;
    /**
     * Janela de 24h do WhatsApp (API nativa da Meta), contada a partir da
     * ULTIMA MENSAGEM RECEBIDA do cliente. Fora dela, texto livre e recusado
     * pelo provedor e so passa template aprovado — por isso o valor vive no
     * chamado, e nao e recalculado na tela.
     */
    janela?: TicketWindow;
    /** Preview da lista da mesa: evita um lookup por linha. */
    ultimaMensagem?: TicketLastMessage;
    qtdMensagens?: number;
    nivelEscalonamento?: number;
    eventos?: TicketEvent[];
    csat?: TicketCsat;
    /**
     * O chamado ANTERIOR, quando este nasceu da continuacao daquele.
     *
     * Chamado encerrado fora do prazo de reabertura nao volta: abre-se outro.
     * Sem este vinculo o historico se parte — o atendente recebe "o mesmo
     * problema de novo" sem nada que ligue os dois, e quem abriu tem que
     * contar tudo de novo.
     */
    ticketPaiId?: string;
    /** Numero do anterior, denormalizado so para a tela mostrar sem consultar. */
    ticketPaiNumero?: string;
    modifiedOn?: string;
}

export interface TicketOrigin {
    canal: TicketChannel;
    channelId?: string;
    contatoId?: string;
    iduserSolicitante?: number;
}

export interface TicketSla {
    prazoResposta?: string;
    prazoSolucao?: string;
    /** Minutos acumulados em AGUARDANDO_CLIENTE / PAUSADO. */
    minPausa?: number;
    respostaEstourou?: boolean;
    solucaoEstourou?: boolean;
}

export interface TicketDates {
    abertura?: string;
    primeiraResposta?: string;
    resolucao?: string;
    encerramento?: string;
}

export interface TicketWindow {
    ultimaEntradaEm?: string;
    expiraEm?: string;
}

export interface TicketLastMessage {
    texto?: string;
    autorTipo?: MessageAuthorType;
    em?: string;
}

/**
 * Evento da timeline. Fica EMBUTIDO no chamado: e append-only, chega a
 * dezenas de itens (nao milhares) e e sempre lido junto com o chamado.
 */
export interface TicketEvent {
    tipo: TicketEventType;
    de?: string;
    para?: string;
    iduser?: number;
    obs?: string;
    em: string;
}

export interface TicketCsat {
    nota?: number;
    comentario?: string;
    em?: string;
}

/**
 * Mensagem da conversa. Colecao SEPARADA do chamado: uma conversa de WhatsApp
 * passa de centenas de mensagens, e array embutido reescreveria o documento
 * inteiro a cada envio, com teto de 16 MB por documento.
 */
export interface TicketMessage {
    _id?: string;
    ticketId: string;
    seq: number;
    autor: TicketMessageAuthor;
    canal: TicketChannel;
    texto: string;
    /** true = nota interna, nao sai para o cliente. */
    interna: boolean;
    /** Id da mensagem no provedor. Unico por canal — barra reentrega. */
    msgIdCanal?: string;
    envio?: TicketMessageDelivery;
    anexos?: TicketAttachment[];
    /** Ponteiro para o payload cru em Mktpwebhooklog. */
    webhook?: { idhook?: number; requestId?: string };
    em: string;
}

export interface TicketMessageAuthor {
    tipo: MessageAuthorType;
    iduser?: number;
    contatoId?: string;
    nome?: string;
}

export interface TicketMessageDelivery {
    status: MessageDeliveryStatus;
    erro?: string;
}

export interface TicketAttachment {
    nome: string;
    mime?: string;
    tamanho?: number;
    ref?: string;
}

/**
 * Sessao de conversa por canal/contato: decide se a mensagem que chegou
 * continua um chamado ou abre outro.
 */
export interface TicketSession {
    _id?: string;
    canal: TicketChannel;
    identificador: string;
    contatoId?: string;
    ticketId?: string;
    estado: SessionState;
    /**
     * Existe enquanto a sessao vive; e REMOVIDO ($unset) ao encerrar.
     *
     * E o que sustenta o indice unico parcial que garante uma sessao viva por
     * contato. O natural seria filtrar por `estado != 'ENCERRADA'`, mas
     * partialFilterExpression nao aceita $ne — so igualdade, $exists,
     * comparadores de ordem, $type e $and. Dai o campo derivado.
     *
     * REGRA PARA QUEM ESCREVE (TicketServices):
     *   abrir   -> { estado: 'IDENTIFICANDO'|..., ativa: true }
     *   encerrar-> { $set: { estado: 'ENCERRADA' }, $unset: { ativa: '' } }
     */
    ativa?: boolean;
    /** sessionId usado no AiServices/converse. */
    aiSessionId?: string;
    contexto?: Record<string, unknown>;
    ultimaMsgEm?: string;
    /** TTL index: o Mongo limpa sessao morta sozinho. */
    expiraEm?: string;
}

/**
 * Funil unico de entrada de TODOS os canais, ja normalizado.
 *
 * Nao guarda o payload cru do provedor — esse fica em `Mktpwebhooklog`, para
 * onde `origem.requestId` aponta. Canais externos sao gravados aqui por um
 * fluxo TemAPI (no DATABASE); o canal APP e gravado pelo Java direto.
 */
export interface TicketInboxItem {
    _id?: string;
    canal: TicketChannel;
    /** Numero/bot que recebeu. */
    identificadorCanal?: string;
    /** Quem falou. Nulo quando canal === 'APP'. */
    identificadorContato?: string;
    nomeContato?: string;
    /** Id no provedor. Unico por canal: rejeita reentrega no enfileiramento. */
    msgIdCanal?: string;
    texto?: string;
    anexos?: TicketAttachment[];
    origem?: TicketInboxOrigin;
    /** Canal APP: resposta em chamado ja existente. */
    ticketIdSugerido?: string;
    status: InboxStatus;
    erro?: string;
    tentativas?: number;
    recebidoEm?: string;
    processadoEm?: string;
    expiraEm?: string;
}

export interface TicketInboxOrigin {
    /** Externos: aponta para o documento em Mktpwebhooklog. */
    idhook?: number;
    requestId?: string;
    /** Canal APP: quem abriu. */
    iduser?: number;
}
